import { Trans } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DashboardExperimentArm } from '@/api/core-api/types.gen';
import { AvatarCard } from '@/components/avatar-card/avatar-card';
import { InfiniteScrollTrigger } from '@/components/foundations/infinite-scroll-trigger';
import { ReferralCard } from '@/components/referral-cards/referral-card';
import { useInfiniteMatchedProjects } from '@/hooks/api';
import { useProfile } from '@/hooks/api/use-profile';
import { useTopPicks } from '@/hooks/api/use-top-picks';
import { useCaptureParticipantUtmFromSearch } from '@/hooks/use-capture-participant-utm-from-search';
import { useDebounced } from '@/hooks/use-debounced';
import { useLinkedinVolOAuthReturn } from '@/hooks/use-linkedin-vol-oauth-return';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import {
  armForAnalytics,
  resolveDashboardExperimentArm,
} from '@/lib/experiments/dashboard-matched-surfacing';
import { getMessengerShareLink, getReferralLink } from '@/utils/referral-links';

import { ActiveProjectsCard } from './-components/active-projects-card';
import { B2bUnlockBanner } from './-components/b2b-unlock-banner';
import { CampaignInviteBanner } from './-components/campaign-invite-banner';
import { ProjectCard } from './-components/project-card';
import { ProjectsErrorState } from './-components/projects-error-state';
import { ProjectsSearchBar } from './-components/projects-search-bar';
import {
  TopPicksSection,
  TopPicksSkeleton,
} from './-components/top-picks-section';
import { projectsSearchSchema, type ProjectsSearch } from './-shared';

export const Route = createFileRoute('/participants/_authenticated/projects/')({
  component: ParticipantsDashboard,
  validateSearch: projectsSearchSchema,
  // One arm per visit. `shouldReload: false` keeps search/filter/sort
  // navigations from re-resolving it; `gcTime: 0` drops the match on exit so
  // re-entry runs the loader instead of rendering the cached arm first.
  gcTime: 0,
  shouldReload: false,
  loader: async ({ context }) => ({
    experimentArm: await resolveDashboardExperimentArm(
      context.queryClient,
      context.user.id,
    ),
  }),
});

function ParticipantsDashboard() {
  const { profileId, user } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const analytics = useAnalytics();
  const searchParams = Route.useSearch();
  const { experimentArm } = Route.useLoaderData();
  const isEnrolled = experimentArm !== null;
  const isTreatment = experimentArm === DashboardExperimentArm.TREATMENT;
  // Fetched for both arms: control needs `hasMatch` for the analysis stratum.
  const topPicks = useTopPicks(profileId, { enabled: isEnrolled });
  // A disabled query still returns data cached by an earlier enrolled visit.
  const topPicksData = isEnrolled ? topPicks.data : undefined;

  // Captures UTM/click-ID params for the visitor who was already signed in
  // and landed here straight off the advertised CTA (PX-274).
  useCaptureParticipantUtmFromSearch(searchParams);

  // Onboarding's connect-LinkedIn step returns here after the OAuth
  // round-trip; this surfaces the success/failure toast and strips the
  // linkedin_vol params from the URL.
  useLinkedinVolOAuthReturn(user.id);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteMatchedProjects({
    profileId,
    pageSize: 20,
    kindsOfResearchStudy: searchParams.filter,
    sort: searchParams.sort,
    queryText: searchParams.q || '',
    eligibleOnly: searchParams.eligible,
  });

  const { data: profileData, isLoading: isProfileLoading } = useProfile(
    user.id,
  );
  const showB2bBanner =
    !isProfileLoading && !profileData?.profile.employment?.isEmailVerified;

  // Flatten all pages into a single array of projects
  const updateSearchParams = useCallback(
    (updates: Partial<ProjectsSearch>) => {
      void navigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [navigate],
  );

  const [searchInput, setSearchInput] = useState(searchParams.q ?? '');

  useEffect(() => {
    setSearchInput(searchParams.q ?? '');
  }, []);

  useDebounced(
    () => {
      if (searchInput !== searchParams.q) {
        updateSearchParams({ q: searchInput || undefined });
      }
    },
    300,
    [searchInput],
  );

  const filterValue = searchParams.filter;
  const sortValue = searchParams.sort;
  const eligibleOnly = searchParams.eligible;

  const projects = data?.pages.flatMap((page) => page.results ?? []) ?? [];

  // dashboard_viewed: User arrives on the main projects page (fire once on initial load)
  const hasFiredDashboardView = useRef(false);
  useEffect(() => {
    if (
      isLoading ||
      isProfileLoading ||
      (isEnrolled && topPicks.isPending) ||
      hasFiredDashboardView.current ||
      !analytics
    )
      return;
    analytics.screen({
      name: 'dashboard',
      attributes: {
        projectsShown: projects.length,
        hasUnlockBanner: showB2bBanner,
        experimentArm: armForAnalytics(experimentArm),
        hasMatch: topPicksData ? topPicksData.matchedCount > 0 : undefined,
        matchedCount: topPicksData?.matchedCount,
        topPicksCount: topPicksData?.projects.length,
        boostedCount: topPicksData?.boostedCount,
      },
    });
    hasFiredDashboardView.current = true;
  }, [
    isLoading,
    isProfileLoading,
    analytics,
    projects.length,
    showB2bBanner,
    experimentArm,
    isEnrolled,
    topPicks.isPending,
    topPicksData,
  ]);

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const context = Route.useRouteContext();

  const referralLink = useMemo(
    () =>
      user.referralCode
        ? getReferralLink(context.env.WEBSITE_URL, user.referralCode)
        : undefined,
    [user.referralCode, context.env.WEBSITE_URL],
  );

  const messengerShareLink = useMemo(
    () =>
      referralLink
        ? getMessengerShareLink(context.env.FACEBOOK_APPID, referralLink)
        : undefined,
    [referralLink, context.env.FACEBOOK_APPID],
  );

  if (error) {
    return (
      <div className="mx-auto max-w-(--max-width-container) px-4 py-8">
        <ProjectsErrorState onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-(--max-width-container) px-4 py-8 md:px-8 lg:grid-cols-12">
      {/* min-w-0: below lg the Top picks scroller would otherwise widen the column */}
      <section className="flex min-w-0 flex-col gap-2 lg:col-span-8">
        {/* Invitation-only campaign nudge; renders nothing for everyone else.
            First on every breakpoint: it is the one thing an invitee came for. */}
        <CampaignInviteBanner userId={user.id} />
        {/* Mobile only, under the campaign banner; the sidebar shows it on desktop. */}
        <div className="mb-6 lg:hidden">
          <ActiveProjectsCard userId={user.id} />
        </div>
        {showB2bBanner && <B2bUnlockBanner displayCta={true} />}
        {/* On error the strip drops out; the feed below is unaffected. */}
        {isTreatment && topPicks.isPending && <TopPicksSkeleton />}
        {isTreatment && topPicksData && (
          <TopPicksSection
            topPicks={topPicksData}
            experimentArm={DashboardExperimentArm.TREATMENT}
          />
        )}
        {/* Search bar */}
        <div className="mb-4">
          <ProjectsSearchBar
            searchQuery={searchInput}
            onSearchChange={setSearchInput}
            filterValue={filterValue}
            onFilterChange={(filter) => {
              analytics?.ui({
                action: 'changed',
                actionSubject: 'dashboardFilter',
                actionSubjectId: 'research-type',
                source: 'dashboard',
                attributes: {
                  filterType: 'format',
                  filterValue: filter.join(','),
                },
              });
              updateSearchParams({
                filter,
              });
            }}
            sortValue={sortValue}
            onSortChange={(sort) => {
              analytics?.ui({
                action: 'changed',
                actionSubject: 'dashboardFilter',
                actionSubjectId: 'sort',
                source: 'dashboard',
                attributes: {
                  filterType: 'sort',
                  sortBy: sort,
                },
              });
              updateSearchParams({ sort });
            }}
            eligibleOnly={eligibleOnly}
            onEligibleOnlyChange={(eligible: boolean) => {
              analytics?.ui({
                action: 'toggled',
                actionSubject: 'dashboardFilter',
                actionSubjectId: 'eligible-only',
                source: 'dashboard',
                attributes: {
                  filterType: 'eligible_only',
                  filterValue: String(eligible),
                },
              });
              updateSearchParams({ eligible });
            }}
          />
        </div>

        <h2 className="mb-2 text-md font-semibold text-tertiary">
          <Trans>Available projects</Trans>
        </h2>

        {isLoading && (
          <div className="flex flex-col gap-6">
            <ProjectCardSkeletons count={10} />
          </div>
        )}
        {!isLoading && projects.length === 0 && <ProjectsEmptyState />}
        {!isLoading && projects.length > 0 && (
          <div className="flex flex-col gap-6">
            {projects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                sort={sortValue}
                index={index}
                surface="feed"
                experimentArm={experimentArm}
              />
            ))}

            {/* Infinite scroll trigger */}
            <InfiniteScrollTrigger
              onLoadMore={handleLoadMore}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
            />

            {/* Loading indicator for next page */}
            {isFetchingNextPage && <ProjectCardSkeletons count={3} />}

            {/* End of list indicator */}
            {!hasNextPage && <ProjectsEndOfList />}
          </div>
        )}
      </section>

      <section className="hidden flex-col gap-4 lg:col-span-4 lg:ml-6 lg:flex">
        <AvatarCard user={user} />
        {/* ActiveProjectsCard - hidden on mobile (shown at top), visible on desktop */}
        <ActiveProjectsCard userId={user.id} />
        {referralLink && (
          <ReferralCard
            variant="compact"
            referralLink={referralLink}
            messengerShareLink={messengerShareLink}
          />
        )}
      </section>
    </div>
  );
}

function ProjectCardSkeleton({ delay = 0 }: { delay?: number }) {
  return (
    <div
      className="animate-pulse rounded-[20px] border-[1.5px] border-secondary bg-primary p-6"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header row: Research badge + Price */}
      <div className="mb-2 flex items-center justify-between">
        <div className="h-6 w-28 rounded-full bg-alpha-black-10" />
        <div className="flex items-center gap-2">
          <div className="h-7 w-12 rounded bg-alpha-black-10" />
          <div className="h-5 w-10 rounded bg-alpha-black-10" />
        </div>
      </div>

      {/* Title */}
      <div className="mb-1 h-7 w-3/4 rounded bg-alpha-black-10" />

      {/* Description - 2 lines */}
      <div className="mb-4 space-y-2">
        <div className="h-4 w-full rounded bg-alpha-black-10" />
        <div className="h-4 w-2/3 rounded bg-alpha-black-10" />
      </div>

      {/* Footer row: Duration, Date, Match status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Duration with icon */}
          <div className="flex items-center gap-2">
            <div className="size-5 rounded bg-alpha-black-10" />
            <div className="h-5 w-14 rounded bg-alpha-black-10" />
          </div>
          {/* Date with icon */}
          <div className="flex items-center gap-2">
            <div className="size-5 rounded bg-alpha-black-10" />
            <div className="h-5 w-16 rounded bg-alpha-black-10" />
          </div>
        </div>
        {/* Match status badge */}
        <div className="h-5 w-24 rounded bg-alpha-black-10" />
      </div>
    </div>
  );
}

function ProjectCardSkeletons({ count = 10 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <ProjectCardSkeleton key={i} delay={i * 75} />
      ))}
    </>
  );
}

function ProjectsEmptyState() {
  return (
    <div className="py-12 text-center">
      <p className="text-secondary">
        <Trans>No available projects at the moment. Check back soon!</Trans>
      </p>
    </div>
  );
}

function ProjectsEndOfList() {
  return (
    <div className="py-4 text-center">
      <p className="text-sm text-tertiary">
        <Trans>You've seen all available projects</Trans>
      </p>
    </div>
  );
}
